import Node from './Node';
import NodeValue from "./NodeValue";

class Tree {
    /**
     * @param amountOfLevels {Number} - число уровней дерева
     */
    constructor (amountOfLevels) {
        const rootValue = new NodeValue({id: 0, name: 'ROOT'}, 0, true);
        /**
         * @type {Node}
         */
        this.root = new Node(rootValue, null, []);

        /**
         * @type {Number}
         */
        this.amountOfLevels = amountOfLevels;

        /**
         * @type {number}
         */
        this.amountOfChosenLeafs = 0;

        // обновляется amountOfChosenLeafs для хранения числа выбранных вершин
        this._updateTreeState();
    }

    /**
     * @returns {Node}
     */
    getRoot() {
        return this.root;
    }

    /**
     * true, если в дереве нет узлов
     * @returns {boolean}
     */
    isEmpty() {
        return (this.root.children.length === 0);
    }

    getAmountOfChosenLeafs() {
        return this.amountOfChosenLeafs;
    }

    fullySelected() {
        const rootValue = this.root.value;
        return (rootValue.isChosen && !rootValue.isPartiallyChosen);
    }

    /**
     * Добавление вершины
     * @param nodeValue {NodeValue}
     * @param parentId {int} - id родительской вершины
     * @returns {boolean} - возвращает true при успешном добавлении вершины в дерево
     * иначе - false.
     */
    addNodeByParentId (nodeValue, parentId) {
        if (this.root === null) {
            this.root = new Node(nodeValue, null, []);
            return true;
        }

        const parentLevel = nodeValue.nodeLevel - 1;
        let parent = this.searchNodeById(parentLevel, parentId, this.root);

        if (parent !== null) {
            let newNode = new Node(nodeValue, parent, []);
            parent.children.push(newNode);
            return true;
        }

        return false;
    }

    /**
     * Поиск вершины.
     * Метод принимает следующие параметры:
     * @param level - уровень вершины,
     * @param id - id вершины,
     * @param node - параметр типа Node для перемещения по вершинам;
     * @returns {null|*} - возвращает объект типа Node (найденная вершина)
     * при успешной попытке поиска, иначе - null.
     */
    searchNodeById (level, id, node) {
        if (node.value.nodeLevel === level &&
            node.value.obj.id === id) {
            return node;
        }

        for (let i = 0; i < node.children.length; i++) {
            let foundNode = this.searchNodeById(level, id, node.children[i]);
            if (foundNode !== null) {
                return foundNode;
            }
        }

        return null;
    }

    /**
     * Развёртывание списка потомков вершины.
     * Метод принимает следующие параметры:
     * @param level - уровень вершины,
     * @param id - id вершины.
     */
    expandNode (level, id) {
        let node = this.searchNodeById (level, id, this.root);
        if (node !== null && node.children.length > 0) {
            node.value.isExpanded = !node.value.isExpanded;
        }
    }

    /**
     * Выбор вершины по ее уровню и id.
     * Более высокоуровненвый метод, нежели markNodeAsChosen
     * @param level - уровень вершины,
     * @param id - id вершины.
     */
    chooseNode (level, id) {
        let node = this.searchNodeById(level, id, this.root);
        this.markNodeAsChosen(node, node.value.isChosen);
        this.refreshParent(node);
        this._updateTreeState();
    }

    /**
     * Пометить вершину как выбрана/не выбрана.
     * Метод принимает следующие параметры:
     * @param node - параметр типа Node для перемещения по вершинам
     * @param chosen - параметр типа bool
     */
    markNodeAsChosen (node, chosen) {
        if (node !== null) {
            let isNodeChosen = chosen;

            if (chosen && node.value.isPartiallyChosen) {
                isNodeChosen = !isNodeChosen;
            }
            else {
                node.value.isChosen = !isNodeChosen;
            }
            node.value.isPartiallyChosen = false;

            node.children.map(child => this.markNodeAsChosen(child, isNodeChosen));
        }
    }

    /**
     * Присваивание одного состояния (выбраны/не выбраны) всем вершинам.
     * Метод принимает следующий параметр:
     * @param node - параметр типа Node для перемещения по вершинам,
     * @param choice - состояние (true/false).
     */
    _chooseOrCancelAllNodes (node, choice) {
        if (node !== null) {
            node.value.isChosen = choice;
            node.value.isPartiallyChosen = false;

            node.children.map(child => this._chooseOrCancelAllNodes(child, choice));
        }
    }

    /**
     * @param choice {boolean}
     */
    selectOrDropAllNodes(choice) {
        this._chooseOrCancelAllNodes(this.root, choice);
        this._updateTreeState();
    }

    /**
     * все узлы будут выбраны, если есть хотя бы один невыбранный узел
     * иначе, если все узлы выбраны, все узлы будут сброшены
     */
    selectOrDropAllNodesByDefaultStrategy () {
        const choice = !this.fullySelected();
        this.selectOrDropAllNodes(choice);
    }

    /**
     * Обновление состояния вершин-предков (выбраны/частично выбраны/не выбраны).
     * Метод принимает следующий параметр:
     * @param node - параметр типа Node для перемещения по вершинам.
     */
    refreshParent (node) {
        if (node !== null && node.parent !== null) {
            if (node.value.isChosen) {
                node.parent.value.isChosen = true;

                node.parent.value.isPartiallyChosen =
                    !this.isParentFullyChosen(node.parent);
            }
            else {
                node.parent.value.isChosen =
                    node.parent.value.isPartiallyChosen =
                        !this.isParentNoneChosen(node.parent);
            }

            this.refreshParent(node.parent);
        }
    }

    /**
     * Проверка состояния вершины-родителя (родитель полностью выбран?).
     * Метод принимает следующий параметр:
     * @param parent - параметр типа Node для перемещения по родительский вершинам;
     * @returns {boolean} - возвращает true, если все потомки родителя выбраны, иначе - false.
     */
    isParentFullyChosen (parent) {
        return parent.children.length === parent.children.filter(child =>
            child.value.isChosen &&
            !child.value.isPartiallyChosen).length;
    }

    /**
     * Проверка состояния вершины-родителя (родитель полностью не выбран?).
     * Метод принимает следующий параметр:
     * @param parent - параметр типа Node для перемещения по родительский вершинам;
     * @returns {boolean} - возвращает true, если ни один потомок родителя не выбран, иначе - false.
     */
    isParentNoneChosen (parent) {
        return parent.children.length === parent.children.filter(child =>
            !child.value.isChosen).length;
    }

    /**
     * Обновление числа выбранных листов
     */
    _updateTreeState () {
        let result = this.countChosenLeafs({count: 0, leaf: ''}, this.root);
        this.amountOfChosenLeafs = result.count;
    }

    /**
     * Подсчёт всех выбранных вершин-листов(вершины самого нижнего уровня).
     * Метод принимает следующие параметры:
     * @param result - параметр, содержащий поля:
     * * count - счётчик,
     * * leaf - название вершины-листа,
     * @param node - параметр типа Node для перемещения по вершинам;
     * @returns {*} - возвращается result.
     */
    countChosenLeafs (result, node) {

        if (node.value.nodeLevel === this.amountOfLevels &&
            node.value.isChosen) {
            result.count++;
            result.leaf = node.value.obj.name;
        }

        node.children.map(child => this.countChosenLeafs(result, child));

        return result;
    }

    /**
     * Добавление определённых вершин.
     * Метод принимает следующие параметры:
     * @param path - массив, содержащий вершины для добавления
     * @param node - параметр типа Node для перемещения по вершинам.
     */
    addNodesByPath (path, node) {
        let currentNode = node;
        let nextNode;

        for (let i = 0; i < path.length; i++) {
            nextNode = path[i];

            if (!currentNode.children.includes(nextNode)) {
                currentNode.children.push(nextNode);
            }

            currentNode = nextNode;
        }
    }

    /**
     * Формирование дерева, содержащего вершины,
     * значение которых включает подстроку name.
     * Метод принимает следующие параметры:
     * @param name - подстрока,
     * @param pathToNode - формирующийся массив, содержащий вершины,
     * * которые ведут к нужной вершине(включая её),
     * @param node - параметр типа Node для перемещения по вершинам.
     */
    _formFilteredTree (name, pathToNode, node) {
        if (!node.value.obj.name.toLowerCase().includes(name) &&
            node.value.nodeLevel === this.amountOfLevels) {
            return;
        }

        let children = [];
        let path = pathToNode;
        let nodeValue = new NodeValue(
            node.value.obj,
            node.value.nodeLevel,
            true,
            node.value.isPartiallyChosen,
            node.value.isChosen
        );

        let parent = this.root;
        if (path.length !== 0) parent = path[path.length - 1];

        let newNode = new Node(nodeValue, parent, children);

        path.push(newNode);

        if (node.value.obj.name.toLowerCase().includes(name)) {
            this.addNodesByPath(path, this.root);
        }

        node.children.map(child => {
            this._formFilteredTree(name, path, child);
        });

        if (path.length !== 0) path.pop();
    }

    /**
     * Создание отфильтрованного дерева по name.
     * Метод принимает следующие параметры:
     * @param name - подстрока name,
     * @param minNameSize - минимальная длина подстроки;
     * @returns {Tree} - возвращает отфильрованное дерево.
     */
    filterTreeByNodeName (name, minNameSize = 0) {
        if (name.length >= minNameSize) {
            let filteredTreeByName = new Tree(this.amountOfLevels);
            this.root.children.map(child => {
                filteredTreeByName._formFilteredTree(name.toLowerCase(), [], child);
            });

            return filteredTreeByName;
        } else {
            return {};
        }
    }

    /**
     * Копирование состояния(выбрана/частично выбрана/не выбрана)
     * соответсвующих вершин дерева originalTree.
     * Метод принимает следующие параметры:
     * @param node - параметр типа Node для перемещения по вершинам,
     * @param originalTree - дерево, из которого копируются состояния вершин
     */
    copyStateOfChoice (node, originalTree) {
        let originalNode = originalTree.searchNodeById(
            node.value.nodeLevel,
            node.value.obj.id,
            originalTree.root
        );

        node.value.isChosen = originalNode.value.isChosen;
        node.value.isPartiallyChosen = originalNode.value.isPartiallyChosen;

        node.children.map(child => { this.copyStateOfChoice(child, originalTree) });
    }

    /**
     * Получить все полностью выбранные вершины на всех уровнях.
     * Метод возвращает
     * @returns {[]} - массив, содержащий массивы с полностью
     * выбранными вершинами на каждом уровне дерева.
     */
    getChosenNodesFromAllLevels () {
        let chosenNodes = [];

        for (let i = 1; i <= this.amountOfLevels; i++) {
            let nodes = this.getChosenNodesFromLevel(i);
            chosenNodes.push(nodes);
        }

        return chosenNodes;
    }

    /**
     * массив выбранных нод без повторений - индекс массива совпадает с уровнем дерева
     * (i-ый элемент массива содержит выбранные ноды на уровне i)
     *
     * "без повторений" означает, что если выбран родитель, то потомки не добавлены в массив
     * (хотя они тоже выбраны)
     *
     * @returns {Array<Node[]>}
     */
    getChosenNodesWithoutRepeat() {
        let chosenNodes = [];
        for (let i = 0; i <= this.amountOfLevels; i++) {
            chosenNodes[i] = [];
        }
        this._gatherChosenNodesWithoutRepeat(this.root, chosenNodes);
        return chosenNodes;
    }

    /**
     * @param sourceNode {Node}
     * @param chosenNodes {Array[]}
     * @private
     */
    _gatherChosenNodesWithoutRepeat(sourceNode, chosenNodes) {
        // если это корневая нода рекурсивный вызов нужен независимо от ее состояния
        if (sourceNode.parent === null) {
            sourceNode.children.forEach(node => {
                this._gatherChosenNodesWithoutRepeat(node, chosenNodes);
            });
        } else {
            if (!sourceNode.value.isChosen) {
                return;
            }

            if (sourceNode.value.isPartiallyChosen) {
                sourceNode.children.forEach(node => {
                    this._gatherChosenNodesWithoutRepeat(node, chosenNodes);
                })
            } else {
                // добавление полностью выбранной ноды
                chosenNodes[sourceNode.value.nodeLevel].push(sourceNode);
            }
        }
    }

    /**
     * массив с данными выбранных нод без повторений - индекс массива совпадает с уровнем дерева
     * (i-ый элемент массива содержит данные выбранных нод на уровне i)
     *
     * "без повторений" означает, что если выбран родитель, то потомки не добавлены в массив
     * (хотя они тоже выбраны)
     *
     * @returns {Array<Object[]>}
     */
    getChosenNodesDataWithoutRepeat() {
        return this.getChosenNodesWithoutRepeat()
            .map(level => level.map(node => node.value.obj));
    }

    /**
     * Получить все полностью выбранные вершины на уровне level.
     * Метод принимает следующий параметр:
     * @param level {number} - уровень в дереве;
     * @returns {Node[]} - возвращает массив полностью выбранных вершин.
     */
    getChosenNodesFromLevel(level) {
        let chosenNodes = [];
        this._gatherChosenNodes(level, chosenNodes, this.root);

        return chosenNodes;
    }

    /**
     * Получить данные всех полностью выбранных вершин на уровне level
     * @param level {number}
     * @returns {Object[]}
     */
    getChosenNodesDataFromLevel(level) {
        return this.getChosenNodesFromLevel(level).map(node => node.value.obj);
    }

    /**
     * Сбор полностью выбранных вершин на уровне level.
     * Метод принимает следующие параметры:
     * @param level - уровень в дереве,
     * @param chosenNodes - массив выбранных вершин,
     * @param node - параметр типа Node для перемещения по вершинам
     * @private
     */
    _gatherChosenNodes (level, chosenNodes, node) {
        if (node.value.nodeLevel === level
            && node.value.isChosen && !node.value.isPartiallyChosen) {
            chosenNodes.push(node);

            return;
        }

        node.children.map(child => { this._gatherChosenNodes(level, chosenNodes, child) });
    }
}

export default Tree;
